/**
 * Commander Dashboard
 *
 * Overview panel for the command room showing all sectors,
 * pending escalations, and controls for broadcasts.
 *
 * Enhanced with:
 * - Map visualization of all sectors
 * - Sector management cards with remote controls
 * - Organizer assignment
 * - Boundary adjustment suggestions
 * - Add sector functionality
 * - Share command center with deputy role
 */

import { useState, useMemo } from 'react'
import { createPortal } from 'react-dom'
import type {
  SectorInfo,
  EscalatedReport,
  EscalationPriority,
  GeoLocation,
  Participant,
} from '@/lib/types'
import type { RemoteCommandPayload } from '@/lib/hierarchy/commands'
import type { AdjustmentResult } from '@/lib/geo'
import type { ExtendedSectorInfo } from '@/hooks/useHierarchy'
import { SectorManagementCard } from './SectorManagementCard'
import { SectorShareModal } from './SectorShareModal'
import { ShareCommandCenter } from './ShareCommandCenter'
import { CreateSector } from './CreateSector'
import { BoundaryAdjustPanel } from './BoundaryAdjustPanel'
import { POIMiniMap } from '@/components/Map/POIMiniMap'
import { convertSectorsToMapOverlays } from '@/components/Map/hierarchyMapUtils'
import type { POIEntry } from '@/lib/sync'

/**
 * Modal Portal Component
 * Renders modals at the document body level to avoid z-index stacking context issues
 */
function ModalPortal({ children, isOpen }: { children: React.ReactNode; isOpen: boolean }) {
  if (!isOpen) return null
  return createPortal(
    <div className="fixed inset-0 z-[9999] isolate">
      {children}
    </div>,
    document.body
  )
}

interface CommandDashboardProps {
  sectors: SectorInfo[]
  /** Extended sector info with boundary data for map visualization */
  extendedSectors?: ExtendedSectorInfo[]
  pendingEscalations: EscalatedReport[]
  totalParticipants: number
  onAcknowledgeEscalation: (escalationId: string) => void
  onBroadcast: (message: string, targetRooms: string[] | 'all', priority: EscalationPriority) => void
  onOpenSector: (sectorRoomId: string) => void
  /** Command room boundary for map visualization */
  commandBoundary?: { center: GeoLocation; radiusMeters: number }
  /** User's current location */
  userLocation?: GeoLocation | null
  /** POIs from all sectors aggregated */
  aggregatedPOIs?: POIEntry[]
  /** Callback to assign a participant as organizer of a sector */
  onAssignOrganizer?: (sectorRoomId: string, participantPeerId: string, participantPublicKey: string) => void
  /** Callback to share a sector room link */
  onShareSector?: (sectorRoomId: string) => void
  /** Callback to update sector boundary */
  onUpdateSectorBoundary?: (sectorRoomId: string, newCenter: GeoLocation, newRadius: number) => void
  /** Callback to send remote command to a sector */
  onSendCommand?: (sectorRoomId: string, command: RemoteCommandPayload) => void
  /** Participants per sector for assignment display */
  sectorParticipants?: Record<string, Participant[]>
  /** Boundary adjustment suggestion (from auto-boundary feature) */
  boundaryAdjustmentSuggestion?: {
    sectorRoomId: string
    sectorName: string
    currentCenter: GeoLocation
    currentRadius: number
    suggestion: AdjustmentResult
  } | null
  /** Callback when boundary adjustment is applied */
  onApplyBoundaryAdjustment?: (sectorRoomId: string, newCenter?: GeoLocation, newRadius?: number) => void
  /** Callback when boundary adjustment is dismissed */
  onDismissBoundaryAdjustment?: () => void
  /** Room name for sharing */
  roomName?: string
  /** Room ID for sharing */
  roomId?: string
  /** Room secret for sharing */
  roomSecret?: string
  /** Room PIN for sharing */
  roomPin?: string
  /** Callback to create a new sector */
  onCreateSector?: (params: { name: string; center: GeoLocation; radiusMeters: number }) => void
  /** Callback to delete/remove a sector */
  onRemoveSector?: (sectorRoomId: string) => void
}

export function CommandDashboard({
  sectors,
  extendedSectors,
  pendingEscalations,
  totalParticipants,
  onAcknowledgeEscalation,
  onBroadcast,
  onOpenSector,
  commandBoundary,
  userLocation,
  aggregatedPOIs = [],
  onAssignOrganizer,
  onShareSector,
  onUpdateSectorBoundary,
  onSendCommand,
  sectorParticipants = {},
  boundaryAdjustmentSuggestion,
  onApplyBoundaryAdjustment,
  onDismissBoundaryAdjustment,
  roomName = 'Command Room',
  roomId = '',
  roomSecret = '',
  roomPin,
  onCreateSector,
  onRemoveSector,
}: CommandDashboardProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [broadcastMessage, setBroadcastMessage] = useState('')
  const [broadcastTargets, setBroadcastTargets] = useState<string[] | 'all'>('all')
  const [broadcastPriority, setBroadcastPriority] = useState<EscalationPriority>('routine')
  const [showBroadcastPanel, setShowBroadcastPanel] = useState(false)
  const [selectedSectorId, setSelectedSectorId] = useState<string | undefined>()
  const [sharingSectorId, setSharingSectorId] = useState<string | null>(null)
  const [showMap, setShowMap] = useState(true)
  const [showCreateSector, setShowCreateSector] = useState(false)
  const [showShareCommandCenter, setShowShareCommandCenter] = useState(false)
  const [activePanel, setActivePanel] = useState<'overview' | 'sectors' | 'escalations'>('overview')

  // Convert extended sectors to map overlays
  const sectorMapOverlays = useMemo(() => {
    if (!extendedSectors) return []
    return convertSectorsToMapOverlays(extendedSectors)
  }, [extendedSectors])

  // Find sector being shared
  const sharingSector = useMemo(() => {
    if (!sharingSectorId || !extendedSectors) return null
    return extendedSectors.find(s => s.roomId === sharingSectorId)
  }, [sharingSectorId, extendedSectors])

  // Handle share sector
  const handleShareSector = (sectorRoomId: string) => {
    if (onShareSector) {
      onShareSector(sectorRoomId)
    }
    setSharingSectorId(sectorRoomId)
  }

  // Handle sector selection from map
  const handleSectorClick = (sectorRoomId: string) => {
    setSelectedSectorId(prev => prev === sectorRoomId ? undefined : sectorRoomId)
  }

  // Handle send command to sector
  const handleSendCommand = (sectorRoomId: string, command: RemoteCommandPayload) => {
    if (onSendCommand) {
      onSendCommand(sectorRoomId, command)
    }
  }

  const handleSendBroadcast = () => {
    if (broadcastMessage.trim()) {
      onBroadcast(broadcastMessage.trim(), broadcastTargets, broadcastPriority)
      setBroadcastMessage('')
      setShowBroadcastPanel(false)
    }
  }

  const toggleSectorTarget = (sectorId: string) => {
    if (broadcastTargets === 'all') {
      setBroadcastTargets([sectorId])
    } else if (broadcastTargets.includes(sectorId)) {
      const newTargets = broadcastTargets.filter((id) => id !== sectorId)
      if (newTargets.length === 0) {
        setBroadcastTargets('all')
      } else {
        setBroadcastTargets(newTargets)
      }
    } else {
      setBroadcastTargets([...broadcastTargets, sectorId])
    }
  }

  const activeSectors = sectors.filter((s) => s.status !== 'offline')
  // alertSectors unused for now but could be used for warnings in the future
  void sectors.filter((s) => s.status === 'alert')

  // Use extendedSectors if available, otherwise fall back to basic sectors
  const displaySectors = extendedSectors || sectors

  return (
    <div className="bg-gradient-to-b from-[#1a1520] to-[#0d0a10] rounded-xl overflow-hidden border border-purple-500/20 shadow-[0_0_30px_rgba(139,92,246,0.1)]">
      {/* Collapsible Header - Command Console Style */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-3 bg-gradient-to-r from-purple-900/30 via-transparent to-purple-900/30 hover:from-purple-900/50 hover:to-purple-900/50 transition-all duration-300 border-b border-purple-500/20"
      >
        <div className="flex items-center gap-3">
          {/* Animated Command Icon */}
          <div className="relative">
            <span className="text-xl">📡</span>
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-green-500 rounded-full animate-pulse shadow-[0_0_8px_rgba(34,197,94,0.8)]" />
          </div>
          <div className="text-left">
            <h2 className="text-sm font-bold text-white tracking-wide flex items-center gap-2">
              Command Dashboard
              <span className="text-[8px] px-1.5 py-0.5 rounded bg-purple-500/30 text-purple-300 font-mono uppercase tracking-widest border border-purple-500/40">
                ICS
              </span>
            </h2>
            <div className="flex items-center gap-3 text-[10px] text-white/60 mt-0.5 font-mono">
              <span className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-teal/80" />
                {sectors.length} sectors
              </span>
              <span className="text-white/30">|</span>
              <span className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-400/80" />
                {totalParticipants} personnel
              </span>
              {pendingEscalations.length > 0 && (
                <>
                  <span className="text-white/30">|</span>
                  <span className="text-red-400 font-medium flex items-center gap-1 animate-pulse">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 shadow-[0_0_6px_rgba(239,68,68,0.8)]" />
                    {pendingEscalations.length} alerts
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Quick status indicators */}
          <div className="hidden sm:flex items-center gap-1 text-[9px] font-mono text-white/40">
            <span className="px-1.5 py-0.5 rounded bg-green-500/20 text-green-400 border border-green-500/30">
              ACTIVE
            </span>
          </div>
          <span className="text-white/40 text-[10px] font-mono w-6 h-6 flex items-center justify-center rounded bg-white/5 border border-white/10">
            {isExpanded ? '▲' : '▼'}
          </span>
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-purple-500/20">
          {/* Tab Navigation */}
          <div className="flex border-b border-white/10 bg-black/30">
            {[
              { id: 'overview', label: 'Overview', icon: '📊' },
              { id: 'sectors', label: 'Sectors', icon: '📍', count: sectors.length },
              { id: 'escalations', label: 'Alerts', icon: '⚠️', count: pendingEscalations.length },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActivePanel(tab.id as typeof activePanel)}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wider transition-all
                  ${activePanel === tab.id
                    ? 'text-primary border-b-2 border-primary bg-primary/5'
                    : 'text-white/50 hover:text-white/80 hover:bg-white/5'
                  }`}
              >
                <span>{tab.icon}</span>
                <span className="hidden sm:inline">{tab.label}</span>
                {tab.count !== undefined && tab.count > 0 && (
                  <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[9px] font-mono
                    ${tab.id === 'escalations' ? 'bg-red-500/30 text-red-400' : 'bg-white/20 text-white/70'}`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Panel Content */}
          <div className="p-3 space-y-4 max-h-[50vh] overflow-y-auto scrollbar-hide">
            {/* Action buttons row - Always visible */}
            <div className="flex flex-wrap items-center gap-2 pb-3 border-b border-white/10">
              {/* Primary actions */}
              {onCreateSector && (
                <button
                  onClick={(e) => { e.stopPropagation(); setShowCreateSector(true) }}
                  className="px-3 py-1.5 bg-gradient-to-r from-teal/30 to-teal/20 text-teal rounded-lg text-xs font-bold
                    hover:from-teal/40 hover:to-teal/30 transition-all border border-teal/40 shadow-[0_0_10px_rgba(27,127,143,0.2)]
                    flex items-center gap-1.5 uppercase tracking-wider"
                >
                  <span className="text-sm">+</span> Add Sector
                </button>
              )}
              {roomSecret && (
                <button
                  onClick={(e) => { e.stopPropagation(); setShowShareCommandCenter(true) }}
                  className="px-3 py-1.5 bg-gradient-to-r from-purple-500/30 to-purple-600/20 text-purple-300 rounded-lg text-xs font-bold
                    hover:from-purple-500/40 hover:to-purple-600/30 transition-all border border-purple-500/40 shadow-[0_0_10px_rgba(139,92,246,0.2)]
                    flex items-center gap-1.5 uppercase tracking-wider"
                >
                  <span>👑</span> Share Command
                </button>
              )}
              <button
                onClick={(e) => { e.stopPropagation(); setShowBroadcastPanel(!showBroadcastPanel) }}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all border flex items-center gap-1.5 uppercase tracking-wider
                  ${showBroadcastPanel
                    ? 'bg-primary/30 text-primary border-primary/50 shadow-[0_0_15px_rgba(242,13,128,0.3)]'
                    : 'bg-gradient-to-r from-primary/20 to-primary/10 text-primary border-primary/30 hover:from-primary/30 hover:to-primary/20 shadow-[0_0_10px_rgba(242,13,128,0.15)]'
                  }`}
              >
                <span>📢</span> Broadcast
              </button>
              {commandBoundary && (
                <button
                  onClick={(e) => { e.stopPropagation(); setShowMap(!showMap) }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all border flex items-center gap-1.5
                    ${showMap
                      ? 'bg-blue-500/30 text-blue-300 border-blue-500/40 shadow-[0_0_10px_rgba(59,130,246,0.2)]'
                      : 'bg-white/10 text-white/60 border-white/20 hover:bg-white/20'
                    }`}
                >
                  <span>🗺️</span> <span className="hidden sm:inline">{showMap ? 'Hide' : 'Show'}</span> Map
                </button>
              )}
            </div>

            {/* Overview Panel */}
            {activePanel === 'overview' && (
              <>
                {/* Enhanced Quick Stats */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="relative overflow-hidden bg-gradient-to-br from-teal/20 to-teal/5 rounded-xl p-3 border border-teal/30">
                    <div className="absolute top-0 right-0 w-12 h-12 bg-teal/10 rounded-full blur-xl" />
                    <div className="relative">
                      <div className="text-2xl font-bold text-teal font-mono">{sectors.length}</div>
                      <div className="text-[10px] text-teal/70 uppercase tracking-wider font-bold mt-0.5">Sectors</div>
                    </div>
                  </div>
                  <div className="relative overflow-hidden bg-gradient-to-br from-blue-500/20 to-blue-500/5 rounded-xl p-3 border border-blue-500/30">
                    <div className="absolute top-0 right-0 w-12 h-12 bg-blue-500/10 rounded-full blur-xl" />
                    <div className="relative">
                      <div className="text-2xl font-bold text-blue-400 font-mono">{totalParticipants}</div>
                      <div className="text-[10px] text-blue-400/70 uppercase tracking-wider font-bold mt-0.5">Personnel</div>
                    </div>
                  </div>
                  <div className={`relative overflow-hidden rounded-xl p-3 border transition-all
                    ${pendingEscalations.length > 0
                      ? 'bg-gradient-to-br from-red-500/30 to-red-500/10 border-red-500/40 shadow-[0_0_20px_rgba(239,68,68,0.2)]'
                      : 'bg-gradient-to-br from-green-500/20 to-green-500/5 border-green-500/30'
                    }`}>
                    <div className={`absolute top-0 right-0 w-12 h-12 rounded-full blur-xl
                      ${pendingEscalations.length > 0 ? 'bg-red-500/20 animate-pulse' : 'bg-green-500/10'}`} />
                    <div className="relative">
                      <div className={`text-2xl font-bold font-mono
                        ${pendingEscalations.length > 0 ? 'text-red-400' : 'text-green-400'}`}>
                        {pendingEscalations.length > 0 ? pendingEscalations.length : '✓'}
                      </div>
                      <div className={`text-[10px] uppercase tracking-wider font-bold mt-0.5
                        ${pendingEscalations.length > 0 ? 'text-red-400/70' : 'text-green-400/70'}`}>
                        {pendingEscalations.length > 0 ? 'Alerts' : 'All Clear'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sector Status Summary */}
                {sectors.length > 0 && (
                  <div className="bg-black/30 rounded-lg p-3 border border-white/10">
                    <h4 className="text-[10px] uppercase tracking-wider font-bold text-white/60 mb-2">Sector Status</h4>
                    <div className="flex flex-wrap gap-2">
                      {sectors.map((sector) => (
                        <button
                          key={sector.roomId}
                          onClick={() => {
                            setActivePanel('sectors')
                            setSelectedSectorId(sector.roomId)
                          }}
                          className={`px-2 py-1 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 border
                            ${sector.status === 'active' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                              sector.status === 'alert' ? 'bg-orange-500/20 text-orange-400 border-orange-500/30 animate-pulse' :
                              'bg-gray-500/20 text-gray-400 border-gray-500/30'}`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full
                            ${sector.status === 'active' ? 'bg-green-500' :
                              sector.status === 'alert' ? 'bg-orange-500' :
                              'bg-gray-500'}`} />
                          {sector.name}
                          <span className="text-[9px] opacity-70">({sector.participantCount})</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Map visualization (command room view) */}
            {showMap && commandBoundary && (
              <div className="rounded-lg overflow-hidden border border-white/10">
                <POIMiniMap
                  center={commandBoundary.center}
                  radiusMeters={commandBoundary.radiusMeters}
                  userLocation={userLocation ?? null}
                  pois={aggregatedPOIs}
                  canClearPOIs={false}
                  hierarchyType="command"
                  sectors={sectorMapOverlays}
                  commandBoundary={commandBoundary}
                  selectedSectorId={selectedSectorId}
                  onSectorClick={handleSectorClick}
                />
              </div>
            )}

            {/* Boundary adjustment suggestion */}
            {boundaryAdjustmentSuggestion && onApplyBoundaryAdjustment && onDismissBoundaryAdjustment && (
              <BoundaryAdjustPanel
                sectorName={boundaryAdjustmentSuggestion.sectorName}
                currentCenter={boundaryAdjustmentSuggestion.currentCenter}
                currentRadius={boundaryAdjustmentSuggestion.currentRadius}
                suggestion={boundaryAdjustmentSuggestion.suggestion}
                onApply={(newCenter, newRadius) =>
                  onApplyBoundaryAdjustment(
                    boundaryAdjustmentSuggestion.sectorRoomId,
                    newCenter,
                    newRadius
                  )
                }
                onDismiss={onDismissBoundaryAdjustment}
              />
            )}

            {/* Broadcast panel */}
            {showBroadcastPanel && (
              <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl p-4 space-y-4 border border-primary/30 shadow-[0_0_20px_rgba(242,13,128,0.1)]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm font-bold text-white uppercase tracking-wider">
                    <span className="text-lg">📢</span> Broadcast Message
                  </div>
                  <button
                    onClick={() => setShowBroadcastPanel(false)}
                    className="p-1 text-white/40 hover:text-white hover:bg-white/10 rounded transition-colors"
                  >
                    ✕
                  </button>
                </div>

                {/* Target selection */}
                <div className="space-y-2">
                  <label className="text-[10px] text-white/60 uppercase tracking-wider font-bold">Target Sectors</label>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setBroadcastTargets('all')}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all border
                        ${broadcastTargets === 'all'
                          ? 'bg-primary text-white border-primary shadow-[0_0_15px_rgba(242,13,128,0.4)]'
                          : 'bg-white/10 text-white/70 border-white/20 hover:bg-white/20'
                        }`}
                    >
                      All Sectors
                    </button>
                    {sectors.map((sector) => (
                      <button
                        key={sector.roomId}
                        onClick={() => toggleSectorTarget(sector.roomId)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all border
                          ${broadcastTargets !== 'all' && broadcastTargets.includes(sector.roomId)
                            ? 'bg-teal text-white border-teal shadow-[0_0_10px_rgba(27,127,143,0.4)]'
                            : 'bg-white/10 text-white/70 border-white/20 hover:bg-white/20'
                          }`}
                      >
                        {sector.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Priority selection */}
                <div className="space-y-2">
                  <label className="text-[10px] text-white/60 uppercase tracking-wider font-bold">Priority Level</label>
                  <div className="flex gap-2">
                    {([
                      { id: 'routine', label: 'Routine', icon: '🟢', color: 'green' },
                      { id: 'urgent', label: 'Urgent', icon: '🟡', color: 'orange' },
                      { id: 'critical', label: 'Critical', icon: '🔴', color: 'red' },
                    ] as const).map((p) => (
                      <button
                        key={p.id}
                        onClick={() => setBroadcastPriority(p.id)}
                        className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all border flex items-center justify-center gap-1.5
                          ${broadcastPriority === p.id
                            ? p.color === 'red' ? 'bg-red-500/30 text-red-300 border-red-500/50 shadow-[0_0_15px_rgba(239,68,68,0.3)]'
                              : p.color === 'orange' ? 'bg-orange-500/30 text-orange-300 border-orange-500/50 shadow-[0_0_15px_rgba(249,115,22,0.3)]'
                              : 'bg-green-500/30 text-green-300 border-green-500/50 shadow-[0_0_15px_rgba(34,197,94,0.3)]'
                            : 'bg-white/5 text-white/50 border-white/10 hover:bg-white/10'
                          }`}
                      >
                        <span>{p.icon}</span>
                        <span className="hidden sm:inline">{p.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Message input */}
                <div className="space-y-2">
                  <label className="text-[10px] text-white/60 uppercase tracking-wider font-bold">Message</label>
                  <textarea
                    value={broadcastMessage}
                    onChange={(e) => setBroadcastMessage(e.target.value)}
                    placeholder="Enter broadcast message..."
                    className="w-full bg-black/40 text-white placeholder-white/30 rounded-lg p-3
                      text-sm border border-white/20 focus:border-primary/50 focus:outline-none focus:bg-black/50 resize-none font-mono transition-all"
                    rows={3}
                  />
                </div>

                {/* Send button */}
                <button
                  onClick={handleSendBroadcast}
                  disabled={!broadcastMessage.trim()}
                  className={`w-full py-3 rounded-xl font-bold uppercase tracking-wider text-sm transition-all border flex items-center justify-center gap-2
                    ${broadcastMessage.trim()
                      ? 'bg-gradient-to-r from-primary to-primary/80 text-white border-primary/50 shadow-[0_0_20px_rgba(242,13,128,0.4)] hover:shadow-[0_0_30px_rgba(242,13,128,0.6)]'
                      : 'bg-white/5 text-white/30 border-white/10 cursor-not-allowed'
                    }`}
                >
                  <span>📡</span> Transmit Broadcast
                </button>
              </div>
            )}

            {/* Escalations Panel */}
            {activePanel === 'escalations' && (
              <div className="space-y-3">
                {pendingEscalations.length === 0 ? (
                  <div className="text-center py-8 bg-black/20 rounded-xl border border-white/10">
                    <span className="text-4xl mb-3 block">✅</span>
                    <p className="text-white/60 font-medium">No pending alerts</p>
                    <p className="text-white/40 text-xs mt-1">All escalations have been acknowledged</p>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-bold text-white uppercase tracking-wider flex items-center gap-2">
                        <span className="text-red-400 animate-pulse">⚠️</span> Pending Alerts
                      </h3>
                      <span className="text-xs font-mono text-red-400 bg-red-500/20 px-2 py-0.5 rounded border border-red-500/30">
                        {pendingEscalations.length} unacknowledged
                      </span>
                    </div>
                    <div className="space-y-2">
                      {pendingEscalations.map((escalation) => (
                        <EscalationCard
                          key={escalation.id}
                          escalation={escalation}
                          onAcknowledge={() => onAcknowledgeEscalation(escalation.id)}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Sectors Panel */}
            {activePanel === 'sectors' && (
              <div className="space-y-3">
                {displaySectors.length === 0 ? (
                  <div className="text-center py-8 bg-black/20 rounded-xl border border-white/10">
                    <span className="text-4xl mb-3 block">📍</span>
                    <p className="text-white/60 font-medium">No sectors created</p>
                    <p className="text-white/40 text-xs mt-1">Add sectors to organize your teams</p>
                    {onCreateSector && (
                      <button
                        onClick={() => setShowCreateSector(true)}
                        className="mt-4 px-4 py-2 bg-teal/20 text-teal rounded-lg text-xs font-bold
                          hover:bg-teal/30 transition-all border border-teal/30"
                      >
                        + Create First Sector
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-bold text-white uppercase tracking-wider flex items-center gap-2">
                        <span>📍</span> Sector Management
                      </h3>
                      <span className="text-xs font-mono text-teal bg-teal/20 px-2 py-0.5 rounded border border-teal/30">
                        {activeSectors.length}/{sectors.length} active
                      </span>
                    </div>
                    <div className="space-y-2">
                      {displaySectors.map((sector) => {
                        // Type guard to check if it's ExtendedSectorInfo
                        const isExtended = 'centerLat' in sector
                        const extendedSector = isExtended ? (sector as ExtendedSectorInfo) : null

                        return extendedSector ? (
                          <SectorManagementCard
                            key={sector.roomId}
                            sector={extendedSector}
                            participants={sectorParticipants[sector.roomId] || []}
                            onAssignOrganizer={onAssignOrganizer
                              ? (peerId, pubKey) => onAssignOrganizer(sector.roomId, peerId, pubKey)
                              : undefined
                            }
                            onShareSector={() => handleShareSector(sector.roomId)}
                            onEditBoundary={onUpdateSectorBoundary
                              ? () => {
                                  // Open boundary edit mode - could be expanded to open a modal
                                  setSelectedSectorId(sector.roomId)
                                }
                              : undefined
                            }
                            onOpenSector={() => onOpenSector(sector.roomId)}
                            onDeleteSector={onRemoveSector
                              ? () => onRemoveSector(sector.roomId)
                              : undefined
                            }
                            onSendCommand={onSendCommand
                              ? (cmd) => handleSendCommand(sector.roomId, cmd)
                              : undefined
                            }
                            commandBoundary={commandBoundary}
                            isSelected={selectedSectorId === sector.roomId}
                          />
                        ) : (
                          <SectorCard
                            key={sector.roomId}
                            sector={sector}
                            onClick={() => onOpenSector(sector.roomId)}
                          />
                        )
                      })}
                    </div>
                  </>
                )}
              </div>
            )}

          </div>
        </div>
      )}

      {/* Share Sector Modal - Rendered via Portal */}
      <ModalPortal isOpen={!!sharingSector}>
        {sharingSector && (
          <div
            className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto"
            onClick={() => setSharingSectorId(null)}
          >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/90 backdrop-blur-md" />
            {/* Modal Content */}
            <div className="relative z-10 w-full max-w-md my-auto animate-fade-in">
              <SectorShareModal
                sector={sharingSector}
                commandRoomId={roomId}
                commandRoomName={roomName}
                onClose={() => setSharingSectorId(null)}
              />
            </div>
          </div>
        )}
      </ModalPortal>

      {/* Create Sector Modal - Rendered via Portal */}
      <ModalPortal isOpen={showCreateSector && !!onCreateSector}>
        <div
          className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => setShowCreateSector(false)}
        >
          {/* Backdrop with gradient */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/95 via-black/90 to-black/95 backdrop-blur-md" />
          {/* Modal Content */}
          <div
            className="relative z-10 w-full max-w-md my-auto animate-fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <CreateSector
              commandRoomId={roomId}
              commandRoomName={roomName}
              currentLocation={userLocation ?? undefined}
              onCreateSector={(params) => {
                onCreateSector?.(params)
                setShowCreateSector(false)
              }}
              onCancel={() => setShowCreateSector(false)}
            />
          </div>
        </div>
      </ModalPortal>

      {/* Share Command Center Modal - Rendered via Portal */}
      <ModalPortal isOpen={showShareCommandCenter && !!roomSecret}>
        <div
          className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => setShowShareCommandCenter(false)}
        >
          {/* Backdrop with purple tint for command theme */}
          <div className="absolute inset-0 bg-gradient-to-b from-purple-950/95 via-black/95 to-black/95 backdrop-blur-md" />
          {/* Modal Content */}
          <div
            className="relative z-10 w-full max-w-md my-auto animate-fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            {roomSecret && (
              <ShareCommandCenter
                roomId={roomId}
                roomSecret={roomSecret}
                roomName={roomName}
                pin={roomPin}
                onClose={() => setShowShareCommandCenter(false)}
              />
            )}
          </div>
        </div>
      </ModalPortal>
    </div>
  )
}

// ============= Sub-components =============

interface EscalationCardProps {
  escalation: EscalatedReport
  onAcknowledge: () => void
}

function EscalationCard({ escalation, onAcknowledge }: EscalationCardProps) {
  const priorityColors: Record<EscalationPriority, string> = {
    critical: 'border-red-500 bg-red-500/10',
    urgent: 'border-orange-500 bg-orange-500/10',
    routine: 'border-green-500 bg-green-500/10',
  }

  const priorityIcons: Record<EscalationPriority, string> = {
    critical: '🔴',
    urgent: '🟡',
    routine: '🟢',
  }

  const timeAgo = getTimeAgo(escalation.timestamp)

  return (
    <div className={`rounded-lg p-2 border-l-4 ${priorityColors[escalation.priority]}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 text-xs text-white/60">
            <span>{priorityIcons[escalation.priority]}</span>
            <span className="uppercase font-medium">{escalation.priority}</span>
            <span>•</span>
            <span>{escalation.sourceRoomName}</span>
            <span>•</span>
            <span>{timeAgo}</span>
          </div>
          <p className="text-sm text-white mt-1 truncate">
            {getMessagePreview(escalation.originalMessage)}
          </p>
          <p className="text-xs text-white/50 mt-0.5">
            Escalated by {escalation.escalatedByName}
          </p>
        </div>
        <button
          onClick={onAcknowledge}
          className="px-2 py-1 bg-white/10 hover:bg-white/20 text-white/80
            rounded text-xs font-medium transition-colors flex-shrink-0"
        >
          ✓ Ack
        </button>
      </div>
    </div>
  )
}

interface SectorCardProps {
  sector: SectorInfo
  onClick: () => void
}

function SectorCard({ sector, onClick }: SectorCardProps) {
  const statusColors: Record<string, string> = {
    active: 'bg-green-500',
    alert: 'bg-orange-500',
    moving: 'bg-blue-500',
    offline: 'bg-gray-500',
  }

  return (
    <button
      onClick={onClick}
      className="bg-white/5 hover:bg-white/10 rounded-lg p-2 text-left
        transition-colors group"
    >
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-white truncate">{sector.name}</span>
        <span className={`w-2 h-2 rounded-full ${statusColors[sector.status]}`} />
      </div>
      <div className="flex items-center gap-2 mt-1 text-xs text-white/60">
        <span>👥 {sector.participantCount}</span>
        {sector.pendingEscalations > 0 && (
          <span className="text-red-400">⚠️ {sector.pendingEscalations}</span>
        )}
      </div>
      {sector.leadName && (
        <p className="text-xs text-white/40 mt-1 truncate">Lead: {sector.leadName}</p>
      )}
    </button>
  )
}

// ============= Utility functions =============

function getTimeAgo(date: Date | string | number): string {
  const timestamp = date instanceof Date ? date.getTime() : new Date(date).getTime()
  const seconds = Math.floor((Date.now() - timestamp) / 1000)

  if (seconds < 60) return 'Just now'
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
  return `${Math.floor(seconds / 86400)}d ago`
}

function getMessagePreview(message: { type: string; content?: string }): string {
  if (message.type === 'voice') return '🎤 Voice message'
  if (message.type === 'poi') return '📍 POI report'
  return message.content?.slice(0, 100) || 'Message'
}

export default CommandDashboard
